import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import TemplatePreview from './TemplatePreview';
import { examplesOf } from './templateUtils';

/**
 * Enviar una plantilla aprobada: iniciar una conversación nueva o escribir
 * fuera de la ventana de 24 h. Si viene fixedPhone, el número y "Atender yo"
 * quedan fijos (la conversación ya es de quien envía).
 */
export default function StartConversationModal({ fixedPhone, onClose, onStarted }) {
  const { apiFetch } = useApi();
  const [templates, setTemplates] = useState([]);
  const [options, setOptions] = useState({});
  const [phone, setPhone] = useState(fixedPhone || '');
  const [templateId, setTemplateId] = useState('');
  const [vars, setVars] = useState({ header: [], body: [] });
  const [handler, setHandler] = useState('me');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([apiFetch('/api/templates'), apiFetch('/api/templates/options')])
      .then(([l, o]) => { setTemplates(l.filter(t => t.status === 'APPROVED' && t.sendable)); setOptions(o); })
      .catch(e => setError(e.message));
  }, [apiFetch]);

  const tpl = useMemo(() => templates.find(t => String(t.id) === String(templateId)), [templates, templateId]);
  const need = { header: tpl?.header?.vars || 0, body: tpl?.body.vars || 0 };
  const ex = examplesOf(tpl?.components);

  // Plantilla nueva → variables vacías
  useEffect(() => {
    setVars({ header: Array(tpl?.header?.vars || 0).fill(''), body: Array(tpl?.body.vars || 0).fill('') });
  }, [tpl]);

  // V2: prellenar desde Zoho (solo los campos vacíos)
  useEffect(() => {
    if (!options.prefill_enabled || !tpl || phone.replace(/\D/g, '').length < 10) return;
    const t = setTimeout(() => {
      apiFetch(`/api/templates/${tpl.id}/prefill?phone=${encodeURIComponent(phone)}`)
        .then(p => setVars(v => ({
          header: v.header.map((x, i) => x || p.header?.[i] || ''),
          body: v.body.map((x, i) => x || p.body?.[i] || ''),
        })))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [options.prefill_enabled, tpl, phone, apiFetch]);

  function setVar(part, i, value) {
    setVars(v => { const n = { ...v, [part]: [...v[part]] }; n[part][i] = value; return n; });
  }

  async function send(e) {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const r = await apiFetch('/api/conversations/start', {
        method: 'POST',
        body: JSON.stringify({ phone, template_id: Number(templateId), variables: vars, handler: fixedPhone ? 'me' : handler }),
      });
      onStarted(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <form style={s.modal} onClick={e => e.stopPropagation()} onSubmit={send}>
        <h2 style={s.title}>{fixedPhone ? '✉️ Enviar plantilla' : '✉️ Nueva conversación'}</h2>
        <div style={s.cols}>
          <div style={s.fields}>
            <label style={s.label}>Número de WhatsApp
              <input style={s.input} value={phone} disabled={!!fixedPhone} onChange={e => setPhone(e.target.value)}
                placeholder="3001234567 o 573001234567" required />
            </label>
            <label style={s.label}>Plantilla aprobada
              <select style={s.input} value={templateId} onChange={e => setTemplateId(e.target.value)} required>
                <option value="">Elige una plantilla…</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.language} · {t.category})</option>)}
              </select>
            </label>
            {templates.length === 0 && !error && <div style={s.muted}>No hay plantillas aprobadas. El admin las crea en Administración → Plantillas.</div>}
            {Array.from({ length: need.header }, (_, i) => (
              <label key={`h${i}`} style={s.label}>Encabezado {`{{${i + 1}}}`}
                <input style={s.input} value={vars.header[i] || ''} placeholder={ex.header[i] || ''} onChange={e => setVar('header', i, e.target.value)} required />
              </label>
            ))}
            {Array.from({ length: need.body }, (_, i) => (
              <label key={`b${i}`} style={s.label}>Variable {`{{${i + 1}}}`}
                <input style={s.input} value={vars.body[i] || ''} placeholder={ex.body[i] || ''} onChange={e => setVar('body', i, e.target.value)} required />
              </label>
            ))}
            {!fixedPhone && (
              <div style={s.label}>¿Quién atiende la respuesta?
                <label style={s.radio}><input type="radio" checked={handler === 'me'} onChange={() => setHandler('me')} /> 🙋 Atender yo (SARA queda en pausa)</label>
                <label style={s.radio}><input type="radio" checked={handler === 'sara'} onChange={() => setHandler('sara')} /> 🤖 Que responda SARA</label>
              </div>
            )}
          </div>
          <div style={s.preview}>
            <div style={s.muted}>Así lo recibirá el cliente</div>
            {tpl ? <TemplatePreview components={tpl.components} vars={vars} headerImageUrl={tpl.header_image?.url} /> : <div style={s.muted}>Elige una plantilla para ver la vista previa.</div>}
          </div>
        </div>
        {error && <div style={s.errorBox}>⚠️ {error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={s.btn} onClick={onClose}>Cancelar</button>
          <button type="submit" style={s.primaryBtn} disabled={sending || !tpl}>{sending ? 'Enviando…' : 'Enviar plantilla'}</button>
        </div>
      </form>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--raised)', border: '1px solid var(--border-strong)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, color: 'var(--text)', fontFamily: "'Plus Jakarta Sans', sans-serif" },
  title: { margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text)' },
  cols: { display: 'flex', gap: 18, flexWrap: 'wrap' },
  fields: { flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 10 },
  preview: { flex: '0 1 340px', display: 'flex', flexDirection: 'column', gap: 8 },
  label: { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, color: 'var(--text-2)' },
  input: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '8px 10px', color: 'var(--text)', fontSize: 13, outline: 'none', fontFamily: 'inherit' },
  radio: { display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text)', fontSize: 13, cursor: 'pointer' },
  muted: { color: 'var(--text-3)', fontSize: 12 },
  errorBox: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13 },
  btn: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '8px 14px', color: 'var(--text)', fontSize: 13, cursor: 'pointer' },
  primaryBtn: { background: 'var(--accent-gradient)', border: 'none', borderRadius: 8, padding: '8px 16px', color: 'var(--on-accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
};
